import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Skeleton,
  message,
  Drawer,
  Popconfirm,
  Empty,
  Timeline,
  Modal,
  List,
  Select,
} from 'antd';
import { HistoryOutlined, RollbackOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ModuleNav from './ModuleNav';
import {
  fetchMaskingSettings,
  updateMaskingSettings,
  fetchSettingsHistory,
  restoreSettingsVersion,
  listAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  listAgentHistory,
} from '../../services/aiImageMaskingApi';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const DEFAULT_SYSTEM_PROMPT =
  'You are an AI designer overlaying textile swatches on furniture images. Match lighting and perspective, avoid warping.';

const Settings = () => {
  const [form] = Form.useForm();
  const [agentForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentModalVisible, setAgentModalVisible] = useState(false);
  const [agentModalLoading, setAgentModalLoading] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentHistoryVisible, setAgentHistoryVisible] = useState(false);
  const [agentHistoryLoading, setAgentHistoryLoading] = useState(false);
  const [agentHistoryRecords, setAgentHistoryRecords] = useState([]);
  const [agentHistoryName, setAgentHistoryName] = useState('');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMaskingSettings();
      form.setFieldsValue({
        openaiApiKey: data?.openai_api_key || '',
        openaiModel: data?.openai_model || 'gpt-4.1',
        azureBlobConnectionString: data?.azure_blob_connection_string || '',
        systemPrompt: data?.system_prompt || DEFAULT_SYSTEM_PROMPT,
      });
      setLastUpdated(data?.updated_at || null);
      setDefaultSystemPrompt(data?.system_prompt || DEFAULT_SYSTEM_PROMPT);
    } catch (error) {
      message.error(error.message || 'Unable to load AI masking settings');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      await updateMaskingSettings({
        openai_api_key: values.openaiApiKey,
        openai_model: values.openaiModel,
        azure_blob_connection_string: values.azureBlobConnectionString,
        system_prompt: values.systemPrompt,
      });
      message.success('Settings saved');
      setLastUpdated(new Date().toISOString());
    } catch (error) {
      message.error(error.message || 'Unable to save settings');
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await fetchSettingsHistory({ limit: 25 });
      setHistoryItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      message.error(error.message || 'Unable to load version history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleOpenHistory = () => {
    setHistoryVisible(true);
    loadHistory();
  };

  const handleRestore = async (historyId) => {
    setRestoringId(historyId);
    try {
      await restoreSettingsVersion(historyId);
      message.success('Settings restored from history');
      await loadSettings();
      await loadHistory();
    } catch (error) {
      message.error(error.message || 'Unable to restore version');
    } finally {
      setRestoringId(null);
    }
  };

  const stringifyChangedFields = (fields) => {
    if (!fields || typeof fields !== 'object' || Object.keys(fields).length === 0) {
      return 'No fields were recorded for this revision.';
    }
    try {
      return JSON.stringify(fields, null, 2);
    } catch (error) {
      console.warn('Unable to stringify history fields', error);
      return String(fields);
    }
  };

  const getHistorySnippet = (text, expanded) => {
    if (expanded || text.length <= 200) {
      return text;
    }
    return `${text.slice(0, 200)}…`;
  };

  const toggleHistoryEntry = (historyId) => {
    setExpandedHistory((prev) => ({
      ...prev,
      [historyId]: !prev[historyId],
    }));
  };

  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const data = await listAgents();
      setAgents(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      message.error(error.message || 'Unable to load agents');
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const handleAgentModalOpen = (agent = null) => {
    setEditingAgent(agent);
    agentForm.setFieldsValue({
      agentName: agent?.agent_name || '',
      jobDescription: agent?.job_description || '',
      systemPrompt: agent?.system_prompt || form.getFieldValue('systemPrompt') || defaultSystemPrompt,
      tags: agent?.tags || [],
    });
    setAgentModalVisible(true);
  };

  const handleAgentModalCancel = () => {
    setAgentModalVisible(false);
    setEditingAgent(null);
    agentForm.resetFields();
  };

  const handleAgentModalSubmit = async () => {
    try {
      const values = await agentForm.validateFields();
      setAgentModalLoading(true);
      const tagsInput = Array.isArray(values.tags) ? values.tags : (values.tags || '').split(',');
      const tags = tagsInput.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
      const uniqueTags = Array.from(new Set(tags.map((tag) => tag.toLowerCase())))
        .sort((a, b) => a.localeCompare(b))
        .map((key) => tags.find((tag) => tag.toLowerCase() === key));
      const payload = {
        agent_name: values.agentName,
        job_description: values.jobDescription,
        system_prompt: values.systemPrompt,
        tags: uniqueTags,
      };
      if (editingAgent) {
        await updateAgent(editingAgent.id, payload);
        message.success('Agent updated');
      } else {
        await createAgent(payload);
        message.success('Agent created');
      }
      handleAgentModalCancel();
      loadAgents();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(error.message || 'Unable to save agent');
    } finally {
      setAgentModalLoading(false);
    }
  };

  const handleAgentDelete = async (agent) => {
    try {
      await deleteAgent(agent.id);
      message.success('Agent deleted');
      loadAgents();
    } catch (error) {
      message.error(error.message || 'Unable to delete agent');
    }
  };

  const handleAgentHistory = async (agent) => {
    setAgentHistoryVisible(true);
    setAgentHistoryLoading(true);
    setAgentHistoryName(agent.agent_name);
    try {
      const data = await listAgentHistory(agent.id);
      setAgentHistoryRecords(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      message.error(error.message || 'Unable to load agent history');
      setAgentHistoryRecords([]);
    } finally {
      setAgentHistoryLoading(false);
    }
  };

  const renderForm = () => (
    <Form
      layout="vertical"
      form={form}
      requiredMark={false}
      onFinish={handleSubmit}
      initialValues={{
        openaiModel: 'gpt-4.1',
      }}
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Form.Item
            label="OpenAI API Key"
            name="openaiApiKey"
            rules={[{ required: true, message: 'API key is required' }]}
          >
            <Input.Password placeholder="sk-..." />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="OpenAI Model"
            name="openaiModel"
            rules={[{ required: true, message: 'Model is required' }]}
          >
            <Input placeholder="gpt-4.1" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label="Studio Storage Connection String"
            name="azureBlobConnectionString"
            rules={[{ required: true, message: 'Connection string is required' }]}
          >
            <Input.Password placeholder="DefaultEndpointsProtocol=...;AccountName=...;AccountKey=..." />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label="System Prompt"
            name="systemPrompt"
            rules={[{ required: true, message: 'System prompt is required' }]}
          >
            <TextArea rows={6} placeholder="Describe how OpenAI should interpret the masking workflow" />
          </Form.Item>
        </Col>
      </Row>

      <Space className="mt-2" wrap>
        <Button type="primary" size="large" htmlType="submit" loading={saving}>
          Save Settings
        </Button>
        <Button
          size="large"
          icon={<HistoryOutlined />}
          onClick={handleOpenHistory}
        >
          Version History
        </Button>
        {lastUpdated && (
          <Tag color="blue">
            Last updated {new Date(lastUpdated).toLocaleString()}
          </Tag>
        )}
      </Space>
    </Form>
  );

  return (
    <div className="ai-image-masking-page">
      <ModuleNav activeKey="settings" />

      <Row gutter={[24, 24]}>
        <Col xs={24} xl={16}>
          <Card
            title="Ai Tryout Studio Settings"
            className="shadow-sm"
            bodyStyle={{ padding: '24px' }}
          >
            {loading ? <Skeleton active paragraph={{ rows: 8 }} /> : renderForm()}
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card className="shadow-sm" bodyStyle={{ padding: '24px' }}>
            <Title level={4}>How it works</Title>
            <Paragraph>
              Configure the AI pipeline that overlays swatches on user-uploaded
              lifestyle photos. Settings are encrypted at rest.
            </Paragraph>
            <Space direction="vertical" size="large" className="w-100">
              <div>
                <Text strong>OpenAI</Text>
                <Paragraph type="secondary" className="mb-0">
                  Provide a dedicated key &amp; model for vision transformations.
                </Paragraph>
              </div>
              <div>
                <Text strong>Studio Storage</Text>
                <Paragraph type="secondary" className="mb-0">
                  Provide a full connection string so swatches and composites
                  save directly to your tenant’s storage container.
                </Paragraph>
              </div>
              <div>
                <Text strong>System Prompt</Text>
                <Paragraph type="secondary" className="mb-0">
                  This prompt is injected into every OpenAI call to enforce
                  lighting, perspective, and masking expectations across the
                  experience.
                </Paragraph>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="mt-4">
        <Col span={24}>
          <Card
            title="Ai Tryout Agents"
            className="shadow-sm"
            bodyStyle={{ padding: '24px' }}
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAgentModalOpen()}>
                New Agent
              </Button>
            }
          >
            {agentsLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <List
                dataSource={agents}
                locale={{
                  emptyText: 'No agents created yet',
                }}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleAgentModalOpen(item)}
                        key="edit"
                      >
                        Edit
                      </Button>,
                      <Button
                        size="small"
                        icon={<HistoryOutlined />}
                        onClick={() => handleAgentHistory(item)}
                        key="history"
                      >
                        History
                      </Button>,
                      <Popconfirm
                        title="Delete agent?"
                        description="This removes the agent configuration."
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleAgentDelete(item)}
                        key="delete"
                      >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                          Delete
                        </Button>
                      </Popconfirm>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <Text strong>{item.agent_name}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.updated_at ? new Date(item.updated_at).toLocaleString() : '--'}
                          </Text>
                        </div>
                      }
                      description={
                        <Space direction="vertical" size={4} className="w-100">
                          <Text type="secondary">{item.job_description}</Text>
                          {item.tags && item.tags.length > 0 ? (
                            <Space size="small" wrap>
                              {item.tags
                                .slice()
                                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                                .map((tag) => (
                                  <Tag key={`${item.id}-${tag}`}>{tag}</Tag>
                                ))}
                            </Space>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              No tags
                            </Text>
                          )}
                          <Paragraph ellipsis={{ rows: 2 }} type="secondary" className="mb-0">
                            {item.system_prompt}
                          </Paragraph>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Drawer
        width={560}
        title="Ai Tryout Studio · Version History"
        open={historyVisible}
        onClose={() => setHistoryVisible(false)}
        destroyOnClose
      >
        {historyLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : historyItems.length === 0 ? (
          <Empty description="No history captured yet" />
        ) : (
          <Timeline mode="left" className="history-timeline">
            {historyItems.map((item) => {
              const fullText = stringifyChangedFields(item.changed_fields);
              const expanded = !!expandedHistory[item.id];
              const snippet = getHistorySnippet(fullText, expanded);
              const showToggle = fullText.length > 200;
              return (
                <Timeline.Item key={item.id}>
                  <Space direction="vertical" className="w-100">
                    <div className="d-flex justify-content-between flex-wrap gap-2 w-100">
                      <div>
                        <Text strong>{new Date(item.changed_at).toLocaleString()}</Text>
                        <div>
                          <Text type="secondary">Entry #{item.id}</Text>
                        </div>
                      </div>
                      <Popconfirm
                        title="Restore this version?"
                        description="The changed fields will be reverted to this revision."
                        okText="Restore"
                        onConfirm={() => handleRestore(item.id)}
                      >
                        <Button
                          type="link"
                          icon={<RollbackOutlined />}
                          loading={restoringId === item.id}
                        >
                          Restore
                        </Button>
                      </Popconfirm>
                    </div>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        backgroundColor: '#f5f5f5',
                        padding: '12px',
                        borderRadius: 8,
                        marginBottom: 0,
                      }}
                    >
                      {snippet}
                    </pre>
                    {showToggle && (
                      <Button type="link" size="small" onClick={() => toggleHistoryEntry(item.id)}>
                        {expanded ? 'Show Less' : 'More'}
                      </Button>
                    )}
                  </Space>
                </Timeline.Item>
              );
            })}
          </Timeline>
        )}
      </Drawer>

      <Modal
        title={editingAgent ? 'Edit Agent' : 'New Agent'}
        open={agentModalVisible}
        onCancel={handleAgentModalCancel}
        onOk={handleAgentModalSubmit}
        confirmLoading={agentModalLoading}
        destroyOnClose
        width={720}
      >
        <Form layout="vertical" form={agentForm} requiredMark={false}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Agent Name"
                name="agentName"
                rules={[
                  { required: true, message: 'Agent name is required' },
                  { max: 60, message: 'Keep agent name under 60 characters' },
                ]}
              >
                <Input placeholder="e.g., Lifestyle Overlay Specialist" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Job Description"
                name="jobDescription"
                rules={[{ required: true, message: 'Job description is required' }]}
              >
                <Input placeholder="Summarize this agent's responsibility" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Tags" name="tags">
                <Select
                  mode="tags"
                  placeholder="Add tags (press Enter or comma to separate)"
                  tokenSeparators={[',']}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="System Prompt"
                name="systemPrompt"
                rules={[{ required: true, message: 'System prompt is required' }]}
              >
                <TextArea rows={6} placeholder="Provide a specialized prompt for this agent" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={`Agent History · ${agentHistoryName || ''}`}
        open={agentHistoryVisible}
        onCancel={() => setAgentHistoryVisible(false)}
        footer={null}
        destroyOnClose
        width={720}
      >
        {agentHistoryLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : agentHistoryRecords.length === 0 ? (
          <Empty description="No history available for this agent" />
        ) : (
          <Timeline mode="left">
            {agentHistoryRecords.map((entry) => (
              <Timeline.Item key={entry.id}>
                <Space direction="vertical" className="w-100">
                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <Text strong>{entry.changed_at ? new Date(entry.changed_at).toLocaleString() : ''}</Text>
                    <Text type="secondary">Entry #{entry.id}</Text>
                  </div>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: 8,
                      marginBottom: 0,
                    }}
                  >
                    {JSON.stringify(entry.changed_fields, null, 2)}
                  </pre>
                </Space>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Modal>
    </div>
  );
};

export default Settings;

